import { useEffect, useRef, useState } from "react"

import useUser from "@hooks/useUser"

import removeURLSearchParam from "@utils/removeURLSearchParam"

import { UseFilterURLEntryProps } from "@organisms/ObjectsView/ObjectsView.types"
import { parseFilterURLEntries } from "@organisms/ObjectsView/ObjectsView.utils"

/**
 * Enter a list pre-filtered by link: `filter__<property>__<operator>=<value>` parameters join the filters of the
 * tab the link opens, once, and then leave the address bar.
 *
 * That tab is the one a `tab` parameter names, or else the phase's tab the list settles on. A parameter the list
 * does not register stays in the address bar.
 */
export default function useFilterURLEntry(props: UseFilterURLEntryProps) {
    const { filterProperties, activePhase, activeTab, activeTabOverride, appliedFilters, addAppliedFilters } = props

    // The URL is an entry point, not where the filters live, so it is read once, on mount.
    const [linkEntries] = useState(() => parseFilterURLEntries(window.location.search, filterProperties))
    const hasEnteredRef = useRef(false)
    const { isLoading: isUserLoading } = useUser()

    // The filters are stored under the user's company, on the tab the link opens, so they wait for both.
    const isEntryReady =
        !isUserLoading &&
        activeTab?.phase === activePhase &&
        (activeTabOverride === undefined || activeTab.title === activeTabOverride)

    useEffect(() => {
        if (isEntryReady && !hasEnteredRef.current) {
            hasEnteredRef.current = true

            // A link opened twice adds its filters once.
            const newFilters = linkEntries.filters.filter(
                (filter) =>
                    !appliedFilters.some(
                        (appliedFilter) =>
                            appliedFilter.property === filter.property &&
                            appliedFilter.operator === filter.operator &&
                            JSON.stringify(appliedFilter.values) === JSON.stringify(filter.values),
                    ),
            )

            if (newFilters.length > 0) {
                addAppliedFilters(newFilters)
            }

            linkEntries.consumedParameterNames.forEach((parameterName) => removeURLSearchParam(parameterName))
        }
    }, [isEntryReady, linkEntries, appliedFilters, addAppliedFilters])
}
